import React, { useState, useEffect } from 'react';
import { Bars3Icon } from '@heroicons/react/24/outline';
import ModernSidebar from './ModernSidebar';

interface AdminLayoutProps {
  children: React.ReactNode;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);

  // Check if mobile on mount and window resize
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 1024; // lg breakpoint
      setIsMobile(mobile);
      if (!mobile) {
        setShowMobileSidebar(false); // Close mobile sidebar on desktop
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const toggleSidebar = () => {
    if (isMobile) {
      setShowMobileSidebar(!showMobileSidebar);
    } else {
      setIsCollapsed(!isCollapsed);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Overlay */}
      {isMobile && showMobileSidebar && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setShowMobileSidebar(false)}
        />
      )}

      {/* Modern Sidebar */}
      <ModernSidebar 
        isCollapsed={isCollapsed} 
        setIsCollapsed={setIsCollapsed}
        showMobileSidebar={showMobileSidebar}
        setShowMobileSidebar={setShowMobileSidebar}
        isMobile={isMobile}
      />

      {/* Main content */}
      <div className={`transition-all duration-300 ${
        isMobile 
          ? 'ml-0' // Mobile: no margin, sidebar overlays
          : isCollapsed 
            ? 'ml-16' // Desktop collapsed: small margin
            : 'ml-64' // Desktop expanded: full margin
      }`}>
        {/* Top bar with hamburger for mobile */}
        <div className="bg-white shadow-sm border-b border-gray-200 px-4 py-3 lg:px-6">
          <div className="flex items-center justify-between">
            <button
              onClick={toggleSidebar}
              className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 transition-colors"
            >
              <Bars3Icon className="h-6 w-6" />
            </button>
            
            {/* Optional: Add breadcrumbs or page title here */}
            <div className="flex items-center space-x-4">
              {/* Add any top-right actions here */}
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="min-h-screen">
          <div className="py-6">
            <div className="w-full px-4 sm:px-6 lg:px-8">
              {children}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
